// src/DriverDashboard.jsx
// ✅ Live location -> RTDB only
//    - live/busX      (full: lat/lng/accuracy optional)
//    - liveStatus/busX (small: isLive/updatedAtMs)  -> for HomePage (low downloads)
// ✅ Firestore used only for: bus assignment + students panel
// ⚠️ Web limitation: Screen OFF / Background => GPS pause ஆகலாம்

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

import { auth, db, rtdb } from "./firebase";
import { signOut } from "firebase/auth";

import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  orderBy,
  getDoc,
} from "firebase/firestore";

import { ref as rtdbRef, update as rtdbUpdate } from "firebase/database";

const BUS_MAX = 15;
const buses = Array.from({ length: BUS_MAX }, (_, i) => `bus${i + 1}`);
const isValidBusId15 = (x) => /^bus([1-9]|1[0-5])$/i.test(String(x || ""));

export default function DriverDashboard() {
  const navigate = useNavigate();

  const [selectedBus, setSelectedBus] = useState(null); // bus1..bus15
  const [tracking, setTracking] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("Idle");

  // Students panel
  const [showStudents, setShowStudents] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsErr, setStudentsErr] = useState("");
  const [sq, setSq] = useState("");

  const [loggingOut, setLoggingOut] = useState(false);

  const watchIdRef = useRef(null);
  const lastGpsAtRef = useRef(0);

  // ✅ 2 seconds updates (as you requested)
  const lastWriteAtRef = useRef(0);
  const MIN_WRITE_MS = 2000;

  // ✅ Wake Lock (best effort)
  const wakeLockRef = useRef(null);

  // ✅ watchdog to restart watch if stale
  const watchdogRef = useRef(null);

  // ✅ auto-select bus from DriverLogin localStorage (optional)
  useEffect(() => {
    const b = String(localStorage.getItem("driverBusId") || "").toLowerCase();
    if (isValidBusId15(b)) setSelectedBus(b);
  }, []);

  const requestWakeLock = async () => {
    try {
      if (!("wakeLock" in navigator)) return;
      if (wakeLockRef.current) return;
      wakeLockRef.current = await navigator.wakeLock.request("screen");
      wakeLockRef.current.addEventListener("release", () => {
        wakeLockRef.current = null;
      });
    } catch (e) {
      wakeLockRef.current = null;
    }
  };

  const releaseWakeLock = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (e) {
      wakeLockRef.current = null;
    }
  };

  const stopWatchOnly = () => {
    if (watchIdRef.current !== null) {
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
      } catch (e) {}
      watchIdRef.current = null;
    }
  };

  // ✅ Single RTDB multi-path update (saves overhead)
  const writeLive = async (busId, patch) => {
    await rtdbUpdate(rtdbRef(rtdb), patch);
  };

  const startWatchOnly = () => {
    if (!navigator.geolocation) {
      setErr("Geolocation not supported!");
      setStatus("Location error");
      return;
    }
    if (!selectedBus) {
      setErr("Select a bus first!");
      return;
    }
    if (!isValidBusId15(selectedBus)) {
      setErr("Invalid bus selected. Only bus1..bus15 allowed.");
      return;
    }
    if (!auth.currentUser) {
      setErr("Not logged in!");
      return;
    }
    if (watchIdRef.current !== null) return;

    const user = auth.currentUser;
    const busId = selectedBus.toLowerCase();

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords || {};
          const lat = Number(latitude);
          const lng = Number(longitude);
          const now = Date.now();

          lastGpsAtRef.current = now;

          setStatus(
            `LIVE • ${lat.toFixed(6)}, ${lng.toFixed(6)} • ±${Math.round(
              accuracy || 0
            )}m`
          );

          if (now - (lastWriteAtRef.current || 0) < MIN_WRITE_MS) return;
          lastWriteAtRef.current = now;

          // ✅ write both:
          // liveStatus -> small (HomePage)
          // live -> full coords (Track page)
          await writeLive(busId, {
            [`liveStatus/${busId}/isLive`]: true,
            [`liveStatus/${busId}/updatedAtMs`]: now,

            [`live/${busId}/isLive`]: true,
            [`live/${busId}/updatedAtMs`]: now,
            [`live/${busId}/lat`]: lat,
            [`live/${busId}/lng`]: lng,
            [`live/${busId}/accuracy`]: accuracy ?? null,

            // optional:
            [`live/${busId}/driverEmail`]: user.email || null,
          });
        } catch (e) {
          setErr(e?.message || "Failed to update RTDB location");
        }
      },
      (e) => {
        setErr(e?.message || "Location error");
        setStatus("Location error");
        stopWatchOnly(); // allow watchdog to restart
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 20000,
      }
    );
  };

  const startWatchdog = () => {
    if (watchdogRef.current) return;
    watchdogRef.current = setInterval(() => {
      if (!tracking) return;

      if (!document.hidden) requestWakeLock();

      // restart if watch died
      if (watchIdRef.current === null) {
        startWatchOnly();
        return;
      }

      // restart if GPS stale
      const last = lastGpsAtRef.current || 0;
      const STALE_MS = 20000;
      if (last && Date.now() - last > STALE_MS) {
        stopWatchOnly();
        startWatchOnly();
      }
    }, 4000);
  };

  const stopWatchdog = () => {
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  };

  const startTracking = async () => {
    setErr("");
    setStatus("Starting...");

    if (!selectedBus) return setErr("Select a bus first!");
    if (!isValidBusId15(selectedBus)) return setErr("Only bus1..bus15 allowed!");
    if (!navigator.geolocation) return setErr("Geolocation not supported!");

    const user = auth.currentUser;
    if (!user) return setErr("Not logged in!");

    const busId = selectedBus.toLowerCase();

    try {
      await requestWakeLock();

      // ✅ Firestore: assign driver to this bus (NO lat/lng here)
      await setDoc(
        doc(db, "buses", busId),
        {
          name: busId.toUpperCase(),
          driverUid: user.uid,
          driverEmail: user.email || "",
          isLive: true,
          assignedAt: serverTimestamp(),
          assignedAtMs: Date.now(),
        },
        { merge: true }
      );

      // ✅ RTDB: mark live true immediately
      const now = Date.now();
      await writeLive(busId, {
        [`liveStatus/${busId}/isLive`]: true,
        [`liveStatus/${busId}/updatedAtMs`]: now,
        [`live/${busId}/isLive`]: true,
        [`live/${busId}/updatedAtMs`]: now,
        [`live/${busId}/driverEmail`]: user.email || null,
      });

      lastGpsAtRef.current = Date.now();
      setTracking(true);

      startWatchOnly();
      startWatchdog();
      setStatus("LIVE • waiting for GPS...");
    } catch (e) {
      setErr(e?.message || "Failed to start tracking");
      setTracking(false);
      stopWatchOnly();
      stopWatchdog();
      await releaseWakeLock();
      setStatus("Idle");
    }
  };

  const stopTracking = async () => {
    setErr("");
    setStatus("Stopping...");

    setTracking(false);
    stopWatchdog();
    stopWatchOnly();
    await releaseWakeLock();

    const busId = selectedBus ? selectedBus.toLowerCase() : null;

    if (busId && isValidBusId15(busId)) {
      try {
        const now = Date.now();
        await writeLive(busId, {
          [`liveStatus/${busId}/isLive`]: false,
          [`liveStatus/${busId}/updatedAtMs`]: now,

          [`live/${busId}/isLive`]: false,
          [`live/${busId}/updatedAtMs`]: now,
        });
      } catch (e) {}

      try {
        await setDoc(
          doc(db, "buses", busId),
          { isLive: false, updatedAt: serverTimestamp(), updatedAtMs: Date.now() },
          { merge: true }
        );
      } catch (e) {}
    }

    setStatus("Idle");
  };

  // reacquire after background/focus
  useEffect(() => {
    const onVis = () => {
      if (!tracking) return;
      if (!document.hidden) {
        requestWakeLock();
        if (watchIdRef.current === null) startWatchOnly();
      }
    };
    const onFocus = () => {
      if (!tracking) return;
      requestWakeLock();
      if (watchIdRef.current === null) startWatchOnly();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tracking, selectedBus]);

  useEffect(() => {
    return () => {
      stopWatchdog();
      stopWatchOnly();
      try {
        if (wakeLockRef.current) wakeLockRef.current.release();
      } catch (e) {}
      wakeLockRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      try {
        await stopTracking();
      } catch (e) {}
      try {
        await signOut(auth);
      } catch (e) {}
    } finally {
      navigate("/driver-login", { replace: true });
      setLoggingOut(false);
    }
  };

  const openStudentDetails = async () => {
    setStudentsErr("");
    setSq("");

    if (!selectedBus) {
      setStudentsErr("Select a bus first.");
      return;
    }
    if (!isValidBusId15(selectedBus)) {
      setStudentsErr("Only bus1..bus15 allowed.");
      return;
    }

    const u = auth.currentUser;
    if (!u) {
      setStudentsErr("Not logged in.");
      return;
    }

    try {
      const busSnap = await getDoc(doc(db, "buses", selectedBus));
      const driverUid = busSnap.exists() ? busSnap.data()?.driverUid : null;

      if (driverUid && driverUid !== u.uid) {
        setStudentsErr("You are not assigned to this bus. Start Live Location for your bus.");
        return;
      }
      if (!driverUid) {
        setStudentsErr("Start Live Location once to assign yourself to this bus, then open Student Details.");
        return;
      }

      setShowStudents(true);
    } catch (e) {
      setStudentsErr(e?.message || "Failed to verify bus assignment.");
    }
  };

  useEffect(() => {
    if (!showStudents) return;
    if (!selectedBus) return;

    setLoadingStudents(true);
    setStudents([]);
    setStudentsErr("");

    const qRef = query(
      collection(db, "buses", selectedBus, "students"),
      orderBy("updatedAt", "desc")
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setStudents(arr);
        setLoadingStudents(false);
      },
      (e) => {
        setStudentsErr(e?.message || "Permission denied / failed to load students.");
        setLoadingStudents(false);
      }
    );

    return () => unsub();
  }, [showStudents, selectedBus]);

  const filteredStudents = useMemo(() => {
    const t = sq.trim().toLowerCase();
    if (!t) return students;
    return students.filter((s) => {
      const year = String(s.year ?? "").toLowerCase();
      const stop = String(s.stopName ?? "").toLowerCase();
      const email = String(s.updatedByEmail ?? "").toLowerCase();
      return year.includes(t) || stop.includes(t) || email.includes(t);
    });
  }, [students, sq]);

  return (
    <div className="driver-glass">
      <style>{`
        .driver-glass{
          min-height:100vh;
          padding: 48px 0;
          background:
            radial-gradient(900px 600px at 15% 15%, rgba(13,110,253,.28), transparent 60%),
            radial-gradient(900px 600px at 85% 25%, rgba(32,201,151,.18), transparent 60%),
            radial-gradient(1000px 700px at 50% 95%, rgba(111,66,193,.16), transparent 60%),
            linear-gradient(180deg, #060a13 0%, #0b1220 100%);
        }
        .glass-card{
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 22px;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 18px 40px rgba(0,0,0,.42);
        }
        .glass-title{ color: rgba(255,255,255,.92); }
        .glass-sub{ color: rgba(255,255,255,.70); font-size: 13px; }
        .bus-btn{ border-radius: 999px !important; font-weight: 800 !important; }
        .pill{
          border: 1px solid rgba(255,255,255,.14);
          background: rgba(255,255,255,.07);
          border-radius: 14px;
          padding: 12px 12px;
          color: rgba(255,255,255,.90);
        }
        .muted{ color: rgba(255,255,255,.70); font-size: 12px; }
        .hr-soft{ border-color: rgba(255,255,255,.12); }

        .table-wrap{ border-radius: 18px; overflow: hidden; border: 1px solid rgba(255,255,255,.14); }
        table{ color: rgba(255,255,255,.92) !important; margin: 0 !important; }
        thead th{
          position: sticky; top: 0; z-index: 2;
          background: rgba(10,18,48,.80) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-color: rgba(255,255,255,.10) !important;
        }
        td, th{ border-color: rgba(255,255,255,.10) !important; vertical-align: middle !important; }
        tr:hover{ background: rgba(255,255,255,.04); }

        .glass-input{
          background: rgba(255,255,255,.10) !important;
          border: 1px solid rgba(255,255,255,.16) !important;
          color: rgba(255,255,255,.92) !important;
          border-radius: 14px !important;
        }
      `}</style>

      <div className="container">
        <div className="glass-card p-4">
          {showStudents ? (
            <>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h3 className="glass-title fw-bold m-0">
                    Student Details • {selectedBus ? selectedBus.toUpperCase() : "—"}
                  </h3>
                  <div className="glass-sub mt-1">Year + Stop</div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-outline-light btn-sm fw-bold"
                    onClick={() => {
                      setShowStudents(false);
                      setSq("");
                      setStudentsErr("");
                    }}
                  >
                    Back
                  </button>

                  <button className="btn btn-danger btn-sm" onClick={logout} disabled={loggingOut}>
                    Logout
                  </button>
                </div>
              </div>

              <hr className="hr-soft" />

              <div className="row g-2 align-items-center">
                <div className="col-12 col-lg-6">
                  <input
                    className="form-control glass-input"
                    placeholder="Search (year / stop / email)..."
                    value={sq}
                    onChange={(e) => setSq(e.target.value)}
                  />
                </div>
                <div className="col-12 col-lg-6 d-flex justify-content-lg-end">
                  <div className="pill" style={{ width: "fit-content" }}>
                    <div className="muted">Total</div>
                    <div className="fw-bold">{filteredStudents.length}</div>
                  </div>
                </div>
              </div>

              {studentsErr && <div className="alert alert-danger mt-3 mb-0">{studentsErr}</div>}

              <div className="table-wrap mt-3">
                <div className="table-responsive" style={{ maxHeight: "70vh" }}>
                  <table className="table table-bordered align-middle text-center">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>#</th>
                        <th style={{ width: 140 }}>Year</th>
                        <th>Stop</th>
                        <th style={{ width: 230 }}>Updated By</th>
                        <th style={{ width: 210 }}>Updated At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingStudents ? (
                        Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                            <td className="py-3">...</td>
                          </tr>
                        ))
                      ) : filteredStudents.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-4" style={{ color: "rgba(255,255,255,.75)" }}>
                            No student details yet.
                          </td>
                        </tr>
                      ) : (
                        filteredStudents.map((s, idx) => (
                          <tr key={s.id}>
                            <td className="fw-bold">{idx + 1}</td>
                            <td>{s.year ?? "—"}</td>
                            <td style={{ textAlign: "left" }}>{s.stopName ?? "—"}</td>
                            <td style={{ fontSize: 13 }}>{s.updatedByEmail || "—"}</td>
                            <td style={{ fontSize: 13 }}>
                              {s.updatedAt?.toDate ? s.updatedAt.toDate().toLocaleString() : "—"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h3 className="glass-title fw-bold m-0">Driver Panel (RTDB Live)</h3>
                  <div className="glass-sub mt-1">
                    Select bus → Start Live Location.
                  </div>
                </div>

                <button className="btn btn-danger btn-sm" onClick={logout} disabled={loggingOut}>
                  Logout
                </button>
              </div>

              <hr className="hr-soft" />

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="pill">
                    <div className="muted">Selected Bus</div>
                    <div className="fw-bold">{selectedBus ? selectedBus.toUpperCase() : "—"}</div>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="pill">
                    <div className="muted">Status</div>
                    <div className="fw-bold">
                      {tracking ? (
                        <span className="text-success">LIVE</span>
                      ) : (
                        <span className="text-warning">OFF</span>
                      )}
                      <span className="ms-2" style={{ fontSize: 13, color: "rgba(255,255,255,.75)" }}>
                        {status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-title fw-semibold mt-4 mb-2">Select Bus (1..15):</div>
              <div className="d-flex flex-wrap gap-2">
                {buses.map((b) => (
                  <button
                    key={b}
                    className={`btn btn-sm bus-btn ${selectedBus === b ? "btn-primary" : "btn-outline-light"}`}
                    onClick={() => setSelectedBus(b)}
                    disabled={tracking}
                  >
                    {b.toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="mt-4 d-flex gap-2 flex-wrap">
                {!tracking ? (
                  <button className="btn btn-success fw-bold" onClick={startTracking}>
                    Start Live Location
                  </button>
                ) : (
                  <button className="btn btn-warning fw-bold" onClick={stopTracking}>
                    Stop Live Location
                  </button>
                )}

                {selectedBus && (
                  <button
                    className="btn btn-outline-light fw-bold"
                    onClick={() => navigate(`/track/${selectedBus}`)}
                  >
                    Preview Student View
                  </button>
                )}

                <button className="btn btn-outline-light fw-bold" onClick={openStudentDetails} disabled={!selectedBus}>
                  Student Details
                </button>
              </div>

              {studentsErr && <div className="alert alert-warning mt-3 mb-0">{studentsErr}</div>}
              {err && <div className="alert alert-danger mt-3 mb-0">{err}</div>}

              <div className="glass-sub mt-3">
                Tip: Keep phone charging + screen ON for best reliability.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}